import { useMemo } from 'react';
import type { Asset } from '@/types/GraphqlSchema';
import {
  COUNTRY_OVERVIEWS_PATH,
  GLOBAL_SEARCH_ACTION_DETAIL,
  GLOBAL_SEARCH_URL_ACTION,
  GLOBAL_SEARCH_URL_COUNTRY_GEOM,
  GLOBAL_SEARCH_URL_ENTITY,
  GLOBAL_SEARCH_URL_GEOM,
  GLOBAL_SEARCH_URL_ISO_CODE,
  GLOBAL_SEARCH_URL_LEGACY_ID,
  GLOBAL_SEARCH_URL_NAME,
  OIL_AND_GAS_MAP_PATH,
  PORTFOLIO_ANALYSIS_PATH,
} from '@/utils/constants';

type EntityType =
  | 'ASSET'
  | 'BASIN'
  | 'BLOCK'
  | 'COUNTRY'
  | 'COMPANY'
  | 'FACILITY'
  | 'FIELD'
  | 'LNG_PROJECT'
  | 'PIPELINE_NETWORK'
  | 'WELL';

type EntityLinkData = {
  prefix: string;
  entity: EntityType;
  name: string;
  url: string | null;
};

type AssetLinks = {
  country: EntityLinkData | null;
  operators: EntityLinkData[];
  map: string | null;
};

const buildUrl = (basePath: string, params: Record<string, string | undefined>): string => {
  const data: Record<string, string> = {};

  for (const [key, value] of Object.entries(params)) {
    if (value) {
      data[key] = value;
    }
  }

  const searchParams = new URLSearchParams(data);
  return `${basePath}?${searchParams.toString()}`;
};

const getCountryLink = (asset: Asset): EntityLinkData | null => {
  if (!asset.country?.isoCode) return null;
  return {
    prefix: 'country: ',
    entity: 'COUNTRY',
    name: asset.country.displayName ?? asset.country.name ?? '',
    url: buildUrl(COUNTRY_OVERVIEWS_PATH, {
      [GLOBAL_SEARCH_URL_ACTION]: GLOBAL_SEARCH_ACTION_DETAIL,
      [GLOBAL_SEARCH_URL_ENTITY]: 'COUNTRY',
      [GLOBAL_SEARCH_URL_NAME]: asset.country.name ?? undefined,
      [GLOBAL_SEARCH_URL_ISO_CODE]: asset.country.isoCode,
      [GLOBAL_SEARCH_URL_LEGACY_ID]: asset.country.id ?? undefined,
      [GLOBAL_SEARCH_URL_GEOM]: asset.country.geom ?? undefined,
      [GLOBAL_SEARCH_URL_COUNTRY_GEOM]: asset.country.geom ?? undefined,
    }),
  };
};

const getLatestOperators = (asset: Asset) => {
  if (!asset.participations || asset.participations.length === 0) {
    return [];
  }
  const latestEffectiveDate = asset.participations[0]?.effectiveDate;
  const latestParticipations = asset.participations.filter(
    participation => participation.effectiveDate === latestEffectiveDate && participation.company,
  );
  return latestParticipations.filter(participation => participation.isCompanyOperator);
};

const getOperatorLinks = (asset: Asset): EntityLinkData[] => {
  const latestOperators = getLatestOperators(asset);
  return latestOperators
    .filter(operator => operator?.company?.name)
    .map(operator => {
      const companyName = operator.company?.name ?? '';
      return {
        prefix: 'operator: ',
        entity: 'COMPANY' as const,
        name: companyName,
        url: buildUrl(PORTFOLIO_ANALYSIS_PATH, {
          [GLOBAL_SEARCH_URL_ACTION]: GLOBAL_SEARCH_ACTION_DETAIL,
          [GLOBAL_SEARCH_URL_ENTITY]: 'COMPANY',
          [GLOBAL_SEARCH_URL_NAME]: companyName,
        }),
      };
    });
};

const getMapLink = (asset: Asset): string | null => {
  if (!(asset.name && asset.country?.isoCode && asset.legacyId)) return null;
  return buildUrl(OIL_AND_GAS_MAP_PATH, {
    [GLOBAL_SEARCH_URL_ACTION]: GLOBAL_SEARCH_ACTION_DETAIL,
    [GLOBAL_SEARCH_URL_ENTITY]: 'ASSET',
    [GLOBAL_SEARCH_URL_NAME]: asset.name,
    [GLOBAL_SEARCH_URL_ISO_CODE]: asset.country.isoCode,
    [GLOBAL_SEARCH_URL_LEGACY_ID]: String(asset.legacyId),
    [GLOBAL_SEARCH_URL_GEOM]: asset.geom ?? undefined,
  });
};

export const useAssetLinks = (asset: Asset | null): AssetLinks => {
  return useMemo(() => {
    if (!asset) {
      return {
        country: null,
        operators: [],
        map: null,
      };
    }

    return {
      country: getCountryLink(asset),
      operators: getOperatorLinks(asset),
      map: getMapLink(asset),
    };
  }, [asset]);
};

export type { AssetLinks, EntityLinkData, EntityType };
